import { IMainTableColumn } from "components/MainTable/MainTableInterfaces";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ActionPlanDescriptionCell from "./components/ActionPlanDescriptionCell";
import { IActionPlan } from "entities/RCA/ActionPlanEntity/ActionPlanEntityInterfaces";
import ActionPlanDueDateCell from "./components/ActionPlanDueDateCell";
import ActionPlanStatusCell from "./components/ActionPlanStatusCell";
import ActionPlanIssueOrRCACell from "./components/ActionPlanIssueOrRCACell";
import ActionPlanTypeCell from "./components/ActionPlanTypeCell";
import RCAUserWithAvatar from "../../components/RCAUserWithAvatar";
import ActionPlanOptionsMenuCell from "./components/ActionPlanOptionsMenuCell/ActionPlanOptionsMenuCell";
import { ApolloQueryResult, OperationVariables } from "@apollo/client";

interface IRCAActionPlansColumns extends Omit<IMainTableColumn, "render" | "renderExpandedRow"> {
    render: (row: IActionPlan) => string | JSX.Element | number;
}

const useRCAActionPlansColumns = (
    refetch: (variables?: Partial<OperationVariables> | undefined) => Promise<ApolloQueryResult<any>>,
) => {
    const { t } = useTranslation();

    const columns = useMemo((): IRCAActionPlansColumns[] => {
        return [
            {
                id: "number",
                label: t("rca.action_plans.plan_number"),
                hideSortIcon: false,
                customStyles: { width: "10%" },
                render: (row) => <ActionPlanTypeCell actionPlan={row} />,
            },
            {
                id: "type",
                label: `${t("rca.rca_dashboard.issue")}/${t("rca.rca_dashboard.rca")}`,
                hideSortIcon: false,
                customStyles: { width: "10%" },
                render: (row) => <ActionPlanIssueOrRCACell actionPlan={row} />,
            },
            {
                id: "name",
                label: t("rca.action_plans.name"),
                hideSortIcon: false,
                customStyles: { width: "30%" },
                render: (row) => <ActionPlanDescriptionCell actionPlan={row} />,
            },
            {
                id: "due_date",
                label: t("rca.rca_library.due_date"),
                hideSortIcon: false,
                align: "center",
                customStyles: { width: "18%" },
                render: (row) => <ActionPlanDueDateCell actionPlan={row} />,
            },
            {
                id: "status",
                label: t("rca.rca_library.status"),
                hideSortIcon: false,
                customStyles: { width: "10%" },
                render: (row) => <ActionPlanStatusCell actionPlan={row} />,
            },
            {
                id: "assignee",
                label: t("rca.action_plans.owner"),
                customStyles: { width: "15%" },
                hideSortIcon: false,
                render: (row) => (
                    <RCAUserWithAvatar
                        imageUrl={row.assignee?.media?.media_url || ""}
                        name={`${row.assignee?.first_name} ${row.assignee?.last_name}` || ""}
                    />
                ),
            },
            {
                id: "options",
                label: "",
                hideSortIcon: true,
                customStyles: { width: "5%" },
                render: (row) => <ActionPlanOptionsMenuCell actionPlan={row} refetch={refetch} />,
            },
        ];
    }, [t, refetch]);

    return columns;
};

export default useRCAActionPlansColumns;
